import { useState, useEffect } from 'react';
import { DriveFile } from '../lib/services/googleDriveService';

interface MinimizedFile {
  id: string;
  file: DriveFile;
  timestamp: number;
}

const STORAGE_KEY = 'minimized_files';

export function useMinimizedFiles() {
  const [minimizedFiles, setMinimizedFiles] = useState<MinimizedFile[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const files = JSON.parse(stored) as MinimizedFile[];
        setMinimizedFiles(files);
      }
    } catch (error) {
      console.error('Error loading minimized files from localStorage:', error);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Listen for custom events to sync across components in the same tab
  useEffect(() => {
    const handleMinimizedFilesUpdate = (e: CustomEvent) => {
      setMinimizedFiles(e.detail);
    };

    window.addEventListener('minimizedFilesUpdated', handleMinimizedFilesUpdate as EventListener);
    return () => window.removeEventListener('minimizedFilesUpdated', handleMinimizedFilesUpdate as EventListener);
  }, []);

  // Save to localStorage whenever minimizedFiles changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(minimizedFiles));
      
      // Emit custom event to sync across components
      const event = new CustomEvent('minimizedFilesUpdated', {
        detail: minimizedFiles
      });
      window.dispatchEvent(event);
    } catch (error) {
      console.error('Error saving minimized files to localStorage:', error);
    }
  }, [minimizedFiles]);

  const addMinimizedFile = (file: DriveFile) => {
    const minimizedFile: MinimizedFile = {
      id: file.id,
      file,
      timestamp: Date.now(),
    };

    setMinimizedFiles(prev => {
      // Remove existing file with same ID if it exists
      const filtered = prev.filter(f => f.id !== file.id);
      // Add new file at the beginning
      return [minimizedFile, ...filtered];
    });
  };

  const removeMinimizedFile = (fileId: string) => {
    setMinimizedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const isFileMinimized = (fileId: string) => {
    return minimizedFiles.some(f => f.id === fileId);
  };

  const getMinimizedFile = (fileId: string) => {
    return minimizedFiles.find(f => f.id === fileId);
  };

  const clearAllMinimizedFiles = () => {
    setMinimizedFiles([]);
  };

  return {
    minimizedFiles,
    addMinimizedFile,
    removeMinimizedFile,
    isFileMinimized,
    getMinimizedFile,
    clearAllMinimizedFiles,
  };
}